import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type OrgRole = "admin" | "operator" | "viewer";

interface AuthUser {
  id: string;
  email: string;
  name: string;
  isFpzeroAdmin: boolean;
  /** Map of organisationId → role for this user. */
  orgs: Record<string, OrgRole>;
}

interface AuthValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  logout: () => void;
}

const Ctx = createContext<AuthValue | null>(null);

const STORAGE_KEY = "vms-cloud-ui:token";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(token));

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void fetch("/api/auth/me", { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) {
          setUser(null);
          setToken(null);
          localStorage.removeItem(STORAGE_KEY);
          return;
        }
        const json = await res.json();
        setUser((json.data as AuthUser) ?? null);
      })
      .catch(() => {
        setUser(null);
        setToken(null);
        localStorage.removeItem(STORAGE_KEY);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { ok: false as const, error: json.error ?? "Login failed" };
    }
    localStorage.setItem(STORAGE_KEY, json.data.token);
    setToken(json.data.token);
    setUser(json.data.user);
    return { ok: true as const };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
    setToken(null);
  }, []);

  const value = useMemo(
    () => ({ user, token, loading, login, logout }),
    [user, token, loading, login, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside AuthProvider");
  return v;
}

/** Authenticated fetch — auto-attaches Bearer token. */
export function useApi() {
  const { token } = useAuth();
  return useCallback(
    async (path: string, init: RequestInit = {}): Promise<Response> => {
      const headers = new Headers(init.headers);
      if (token) headers.set("Authorization", `Bearer ${token}`);
      if (!headers.has("Content-Type") && init.body && typeof init.body === "string") {
        headers.set("Content-Type", "application/json");
      }
      return fetch(path, { ...init, headers });
    },
    [token],
  );
}

/* ─────────────────────────── RBAC ─────────────────────────── */

/**
 * Permission ids used across the cloud UI. Keep this list narrow — every
 * entry maps to a concrete UI control / route guard. Anything that needs
 * "FPZERO admin only" access checks `user.isFpzeroAdmin` directly.
 */
export type Permission =
  | "drivers:edit"
  | "drivers:lock-to-venue"
  | "events:edit"
  | "bookings:confirm"
  | "templates:edit"
  | "org:settings"
  | "members:invite"
  | "partners:manage"
  | "venue:transfer-request"
  // FPZERO-only
  | "venues:create"
  | "venues:transfer-execute"
  | "releases:pin"
  | "licences:manage"
  | "orgs:manage";

const FPZERO_ONLY: Permission[] = [
  "venues:create",
  "venues:transfer-execute",
  "releases:pin",
  "licences:manage",
  "orgs:manage",
];

const ROLE_PERMS: Record<OrgRole, Permission[]> = {
  admin: [
    "drivers:edit",
    "drivers:lock-to-venue",
    "events:edit",
    "bookings:confirm",
    "templates:edit",
    "org:settings",
    "members:invite",
    "partners:manage",
    "venue:transfer-request",
  ],
  operator: ["drivers:edit", "events:edit", "bookings:confirm", "templates:edit"],
  viewer: [],
};

export function roleAllows(role: OrgRole | undefined, perm: Permission): boolean {
  if (!role) return false;
  return ROLE_PERMS[role].includes(perm);
}

/** Look up the current user's role within a given organisation. */
export function useOrgRole(organisationId: string | null | undefined): OrgRole | null {
  const { user } = useAuth();
  if (!user || !organisationId) return null;
  return user.orgs[organisationId] ?? null;
}

/**
 * Returns a function `(perm, organisationId?) => boolean`. FPZERO admins can
 * do anything. Org users are gated by their role within the supplied org.
 *
 *   const canIDo = useCanIDo();
 *   if (canIDo("members:invite", org.id)) { ... }
 */
export function useCanIDo() {
  const { user } = useAuth();
  return useCallback(
    (perm: Permission, organisationId?: string | null): boolean => {
      if (!user) return false;
      if (user.isFpzeroAdmin) return true;
      if (FPZERO_ONLY.includes(perm)) return false;
      if (!organisationId) return false;
      const role = user.orgs[organisationId];
      return roleAllows(role, perm);
    },
    [user],
  );
}
